/**
 * Event Filtering Utilities
 *
 * Pure functions for filtering events and riders based on `included_rider_ids`.
 * Used by RSVP.tsx, EventDetail.tsx, and Checkin.tsx for client-side filtering.
 *
 * Design principle: an event with no `included_rider_ids` (or empty array) means
 * "all riders invited" — backward compatibility by omission.
 */

import type { Event, Rider } from '../api/rest-client';

/**
 * Determine whether an event is visible to a user given their associated rider IDs.
 *
 * - If the event has no `included_rider_ids` (or it is empty), the event is visible to everyone.
 * - Otherwise the event is visible only when at least one of the user's rider IDs
 *   appears in the event's `included_rider_ids`.
 */
export function isEventVisibleToUser(
  event: Pick<Event, 'included_rider_ids'>,
  userRiderIds: string[],
): boolean {
  const included = event.included_rider_ids;
  if (!included || included.length === 0) {
    return true; // No restriction — visible to all
  }
  return userRiderIds.some(id => included.includes(id));
}

/**
 * Filter a list of riders to only those invited to a given event.
 *
 * - If the event has no `included_rider_ids` (or it is empty), all riders are returned.
 * - Otherwise only riders whose `rider_id` is in `included_rider_ids` are returned.
 */
export function filterRidersByEvent<R extends Pick<Rider, 'rider_id'>>(
  riders: R[],
  event: Pick<Event, 'included_rider_ids'>,
): R[] {
  const included = event.included_rider_ids;
  if (!included || included.length === 0) {
    return riders; // No restriction — all riders
  }
  const includedSet = new Set(included);
  return riders.filter(r => includedSet.has(r.rider_id));
}

/**
 * Compute RSVP counts over only the invited riders for an event.
 *
 * `getRsvpStatusFn` maps a rider ID to its RSVP status string.
 * Returns counts for each status bucket.
 */
export function computeRsvpCounts<R extends Pick<Rider, 'rider_id'>>(
  riders: R[],
  event: Pick<Event, 'included_rider_ids'>,
  getRsvpStatusFn: (riderId: string) => string,
): { yes: number; maybe: number; no: number; none: number } {
  const invited = filterRidersByEvent(riders, event);
  const counts = { yes: 0, maybe: 0, no: 0, none: 0 };
  for (const rider of invited) {
    const status = getRsvpStatusFn(rider.rider_id);
    if (status === 'coming' || status === 'yes') counts.yes++;
    else if (status === 'maybe') counts.maybe++;
    else if (status === 'not-coming' || status === 'no') counts.no++;
    else counts.none++;
  }
  return counts;
}
